import { useEffect, useState } from "react";
import { Box, Button, Flex, FormControl, FormLabel, Input, SimpleGrid, Spinner, Text, useToast } from "@chakra-ui/react";
import { crm } from "../../../theme";
import { UnitProfile } from "../../../models/ExchangeNumerary";
import { getUnitProfiles, saveUnitProfile } from "../../../services/exchangeNumeraryService";

const UNIT_NAMES: Record<number, string> = { 1: "CVF Logística", 2: "CVF Distribuidora", 3: "CVF Rondônia", 4: "W Trading" };

// Limites iguais aos do servidor (UnitProfileMap)
const FIELDS: [keyof UnitProfile, string, number][] = [
  ["displayName", "Nome no documento", 200],
  ["cnpj", "CNPJ", 30],
  ["addressLine", "Endereço", 200],
  ["cityLine", "CEP / cidade / UF", 200],
  ["bankName", "Banco", 200],
  ["agency", "Agência", 30],
  ["account", "Conta corrente", 40],
  ["pixKey", "Chave PIX", 200],
  ["beneficiary", "Favorecido", 200],
];

/** Cabeçalho e dados bancários de cada unidade: usados na solicitação de numerário de câmbio enviada ao cliente. */
const UnitProfiles = () => {
  const toast = useToast();
  const [units, setUnits] = useState<UnitProfile[]>([]);
  const [saved, setSaved] = useState<UnitProfile[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState<number | null>(null);

  useEffect(() => {
    getUnitProfiles()
      .then((list) => { setUnits(list); setSaved(list); })
      .catch(() => toast({ status: "error", title: "Não foi possível carregar as unidades" }))
      .finally(() => setLoading(false));
  }, []);

  const patch = (unitType: number, field: keyof UnitProfile, value: string) =>
    setUnits((list) => list.map((u) => (u.unitType === unitType ? { ...u, [field]: value } : u)));

  const save = async (unit: UnitProfile) => {
    setSaving(unit.unitType);
    try {
      const result = await saveUnitProfile(unit);
      if (!result.success) {
        toast({ status: "error", title: result.notifications?.[0] ?? "Não foi possível salvar" });
        return;
      }
      setSaved((list) => list.map((u) => (u.unitType === unit.unitType ? unit : u)));
      toast({ status: "success", title: `${UNIT_NAMES[unit.unitType]} salva` });
    } catch {
      toast({ status: "error", title: "Não foi possível salvar" });
    } finally {
      setSaving(null);
    }
  };

  if (loading) return <Flex justify="center" py="32px"><Spinner /></Flex>;

  return (
    <Box w="100%">
      <Text fontSize="20px" fontWeight="700" color={crm.text}>Dados das unidades</Text>
      <Text color={crm.textMuted} fontSize="sm" mt="4px" mb="16px">
        Cabeçalho e dados bancários impressos na solicitação de numerário de câmbio de cada unidade.
      </Text>
      {units.map((unit) => {
        const dirty = JSON.stringify(unit) !== JSON.stringify(saved.find((s) => s.unitType === unit.unitType));
        return (
          <Box key={unit.unitType} border={`1px solid ${crm.border}`} borderRadius="12px" p="16px" mb="14px">
            <Flex justify="space-between" align="center" mb="10px">
              <Text fontWeight="700">{UNIT_NAMES[unit.unitType]}</Text>
              <Button size="sm" bg={crm.brand} color="white" _hover={{ bg: crm.brandDark }} isDisabled={!dirty}
                isLoading={saving === unit.unitType} onClick={() => save(unit)}>
                Salvar
              </Button>
            </Flex>
            <SimpleGrid columns={{ base: 1, md: 3 }} spacing="10px">
              {FIELDS.map(([field, label, max]) => (
                <FormControl key={field}>
                  <FormLabel fontSize="12px" color={crm.textMuted} mb="4px">{label}</FormLabel>
                  <Input size="sm" maxLength={max} value={(unit[field] as string) ?? ""} onChange={(e) => patch(unit.unitType, field, e.target.value)} />
                </FormControl>
              ))}
            </SimpleGrid>
          </Box>
        );
      })}
    </Box>
  );
};

export default UnitProfiles;
